import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";

import { ContentPortal, ContentPortalContainer } from "capitalrx-components-2.0/lib/shell";

import { ShellContext } from "../context";
import { useOwnerModule } from "../hooks";

import styles from "./ContentSubscription.module.css";

/** Content subscription component properties. */
export interface ContentSubscriptionProps {
  /** The ID of the module containing the channel. */
  readonly moduleId: string | null;

  /** The ID of the channel. */
  readonly channelId: string;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;

  /** The optional subscription parameters. */
  readonly params?: unknown;

  /** The component children. */
  readonly children?: ReactNode;
}

/** A component that dynamically inserts its content into the specified content channel. Perfect for page-specific content such as menu bars. */
export const ContentSubscription = (props: ContentSubscriptionProps): ReactNode => {
  const { moduleId, channelId, children, params } = props;

  const { registerDynamicChannelSubscription } = useContext(ShellContext);
  const ownerModule = useOwnerModule();
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  const selector = moduleId === null ? `_c_${channelId}` : `_c_${moduleId}_${channelId}`;

  const refHandler = useCallback((ref: HTMLDivElement | null) => {
    setElement(ref);
  }, []);

  const component = useMemo(() => {
    const ContentSubscriptionContainer = () => (
      <ContentPortalContainer className={`${styles.container} ${selector}`} refHandler={refHandler} />
    );
    return ContentSubscriptionContainer;
  }, [selector, refHandler]);

  useEffect(() => {
    if (!ownerModule) {
      console.warn("Content Subscription can only be used from within a module.");
      return;
    }

    const deregister = registerDynamicChannelSubscription(moduleId, channelId, {
      module: ownerModule,
      type: "component",
      priority: 0,
      component,
      params,
    });

    return (): void => {
      deregister();
    };
  }, [moduleId, channelId, component, params, registerDynamicChannelSubscription]);

  if (!element) return null;

  // Legacy callers may pass children as a function; resolve it before portaling.
  let renderChildren = children;
  if (typeof children === "function") {
    // @ts-expect-error -- legacy function-as-children compatibility shim.
    renderChildren = children();
  }

  return <ContentPortal container={element}>{renderChildren}</ContentPortal>;
};

/** The `ActionBar` component properties. */
export interface ActionBarProps {
  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Subscribes a component to an action bar. */
export const ActionBar = (props: ActionBarProps): ReactNode => {
  const { children } = props;

  return (
    <ContentSubscription moduleId={null} channelId="app-action-bars">
      {children}
    </ContentSubscription>
  );
};

/** The `LeftSideBar` component properties. */
export interface LeftSideBarProps {
  /** The sidebar ID. If specified the closed/open state of the sidebar is tracked. */
  readonly id?: string;

  /** The component children. */
  readonly children?: ReactNode;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;
}

/** Subscribes a component to a left side bar. */
export const LeftSideBar = (props: LeftSideBarProps): ReactNode => {
  const { id, children } = props;

  const sidebarParams = useMemo(() => {
    return { id };
  }, [id]);

  return (
    <ContentSubscription moduleId={null} channelId="app-sidebar-left" params={sidebarParams}>
      {children}
    </ContentSubscription>
  );
};

/** The `RightSideBar` component properties. */
export interface RightSideBarProps {
  /** The sidebar ID. If specified the closed/open state of the sidebar is tracked. */
  readonly id?: string;

  /** The component children. */
  readonly children?: ReactNode;

  /**
   * The dependencies associated with the template, the template will be re-calculated if the dependencies change.
   * @deprecated
   */
  readonly dependencies?: ReadonlyArray<unknown>;
}

/** Subscribes a component to a right side bar. */
export const RightSideBar = (props: RightSideBarProps): ReactNode => {
  const { id, children } = props;

  const sidebarParams = useMemo(() => {
    return { id };
  }, [id]);

  return (
    <ContentSubscription moduleId={null} channelId="app-sidebar-right" params={sidebarParams}>
      {children}
    </ContentSubscription>
  );
};

/** The `Task` component properties. */
export interface TaskProps {
  /** The task title. */
  readonly title: ReactNode;

  /** The function to call to close the task. */
  readonly onClose?: () => void;

  /** The component children. */
  readonly children?: ReactNode;
}

/** Registers a task which can be displayed within the Task flyout. */
export const Task = (props: TaskProps): ReactNode => {
  const { title, onClose, children } = props;

  const id = useId();

  const params = useMemo(() => {
    return {
      id,
      title,
      onClose,
    };
  }, [id, title, onClose]);

  return (
    <ContentSubscription moduleId={null} channelId="flyout-tasks" params={params}>
      {children}
    </ContentSubscription>
  );
};
